

import axios from 'axios'
import { fetchAccessToken, handleErrorResponse } from '../../api'



const apiServer = axios.create({
  baseURL: `${window.location.origin}/api/v1/tenants`,
  headers: {
    common: {
      'Content-Type': 'application/json',
    },
  },
})
const CancelToken = axios.CancelToken
const source = CancelToken.source()

apiServer.interceptors.request.use(async (r) => {
  //Obtain and pass along Authorization token
  const authorizationToken = await fetchAccessToken()
  r.headers.Authorization = "Bearer " + authorizationToken

  //Configure the AbortSignal
  if (r.signal) {
    r.signal.onabort = () => {
      source.cancel()
    }
  }
  r.cancelToken = source.token

  return r
})

//API Aborted class
class Aborted extends Error {
  constructor(message, cause) {
    super(message)
    this.aborted = true
    this.cause = cause
  }
}
const tenantAPI = {
  fetchAll: async () => {
    try {
      const authorizationToken = await fetchAccessToken()
      const response = await fetch(`${window.location.origin}/api/v1/tenants/provisioned`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + authorizationToken,
        },
      })

      const responseJSON = await handleErrorResponse(response)
      return responseJSON
    } catch (err) {
      console.error(err)
      throw Error('Unable to fetch tenants')
    }
  },
  fetchAllAxios: async (ops) => {
    const { signal } = ops

    try {
      const response = await apiServer.get('?status=onboarded', { signal })
      return response.data
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('API call cancelled')
        throw new Aborted('Call aborted', err)
      } else {
        console.error(err)
        throw Error('Unable to fetch tenants')
      }
    }
  },
  fetchTenant: async (tenantId) => {
    try {
      const authorizationToken = await fetchAccessToken()
      const response = await fetch(`${window.location.origin}/api/v1/tenants/${tenantId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + authorizationToken,
        },
      })
      const responseJSON = await handleErrorResponse(response)
      return responseJSON
    } catch (err) {
      console.error(err)
      throw Error(`Unable to fetch tenant: ${tenantId}`)
    }
  },
  provisionTenant: async (values) => {
    throw Error('Unsupported operation')
  },
  editTenant: async (values) => {
    try {
      const authorizationToken = await fetchAccessToken()
      const response = await fetch(`${window.location.origin}/api/v1/tenants/${values.id}`, {
        method: 'PUT',
        mode: 'cors',
        body: JSON.stringify({
          ...values,
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: "Bearer " + authorizationToken,
        },
      })
      const responseJSON = await handleErrorResponse(response)
      return responseJSON
    } catch (err) {
      console.error(err)
      throw Error('Unable to edit tenant.')
    }
  },
  enableTenant: async (tenantId, ops) => {
    const { signal } = ops

    try {
      const response = await apiServer.patch(
        `/${tenantId}/enable`,
        { id: tenantId },
        { signal }
      )
      return response.data
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Aborted('Call aborted', err)
      } else {
        console.error(err)
        throw Error(`Unable to enable tenant ${tenantId}`)
      }
    }
  },
  disableTenant: async (tenantId, ops) => {
    const { signal } = ops

    try {
      const response = await apiServer.patch(
        `/${tenantId}/disable`,
        { id: tenantId },
        { signal }
      )
      return response.data
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Aborted('Call aborted', err)
      } else {
        console.error(err)
        throw Error(`Unable to disable tenant ${tenantId}`)
      }
    }
  },
  deleteTenant: async (tenantId, ops) => {
    const { signal } = ops

    try {
      const response = await apiServer.delete(
        `/${tenantId}`,
        {
          data: { id: tenantId },
        },
        { signal }
      )
      return response.data
    } catch (err) {
      if (axios.isCancel(err)) {
        throw new Aborted('Call aborted', err)
      } else {
        console.error(err)
        throw Error(`Unable to delete tenant ${tenantId}`)
      }
    }
  },
  /**
   * Determines if err is from a Cancelled or Aborted request
   * @param err
   */
  isCancel: (err) => {
    if (err.aborted && err.aborted === true) {
      return true
    }
    return false
  },
}

export default tenantAPI
